import { cn } from '@/lib/utils';

type RiskLevel = 'critical' | 'high' | 'medium' | 'low' | 'info';

interface ScoreCardProps {
  title: string;
  score: number;
  maxScore?: number;
  riskLevel?: RiskLevel;
  description?: string;
  size?: 'sm' | 'md' | 'lg';
  onClick?: () => void;
  clickable?: boolean;
}

const riskLevelColors: Record<RiskLevel, string> = {
  critical: 'from-risk-critical to-red-600',
  high: 'from-risk-high to-orange-600',
  medium: 'from-risk-medium to-yellow-600',
  low: 'from-risk-low to-emerald-600',
  info: 'from-risk-info to-blue-600',
};

const ScoreCard = ({
  title,
  score,
  maxScore = 10,
  riskLevel = 'medium',
  description,
  size = 'md',
  onClick,
  clickable = false,
}: ScoreCardProps) => {
  const percentage = (score / maxScore) * 100;
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  const sizeClasses = {
    sm: 'w-24 h-24',
    md: 'w-32 h-32',
    lg: 'w-40 h-40',
  };

  const textSizes = {
    sm: 'text-xl',
    md: 'text-3xl',
    lg: 'text-4xl',
  };

  const handleClick = () => {
    if (clickable && onClick) {
      onClick();
    }
  };

  return (
    <div
      className={cn(
        'bg-zinc-900/50 border border-zinc-800 rounded-lg p-6 flex flex-col items-center text-center',
        clickable &&
          'cursor-pointer hover:shadow-lg hover:scale-[1.02] hover:bg-zinc-800/70 transition-all duration-200'
      )}
      onClick={handleClick}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => e.key === 'Enter' && handleClick() : undefined}
    >
      <div className={cn('relative', sizeClasses[size])}>
        <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
          <circle
            cx="50"
            cy="50"
            r="45"
            fill="none"
            stroke="currentColor"
            strokeWidth="6"
            className="text-zinc-600"
          />
          <circle
            cx="50"
            cy="50"
            r="45"
            fill="none"
            strokeWidth="6"
            strokeLinecap="round"
            className={cn('transition-all duration-1000', `stroke-current text-risk-${riskLevel}`)}
            style={{
              strokeDasharray: circumference,
              strokeDashoffset: strokeDashoffset,
            }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={cn('font-bold text-white', textSizes[size])}>
            {score != null ? score.toFixed(1) : '—'}
          </span>
          <span className="text-zinc-400 text-xs">out of {maxScore}</span>
        </div>
      </div>

      <h3 className="mt-4 font-semibold text-sm uppercase tracking-wider text-zinc-300">
        {title}
      </h3>

      {description && <p className="mt-2 text-xs text-zinc-400">{description}</p>}

      <div
        className={cn(
          'mt-3 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider',
          'bg-gradient-to-r text-white',
          riskLevelColors[riskLevel]
        )}
      >
        {riskLevel}
      </div>

      {clickable && <p className="mt-2 text-xs text-green-400 underline">Click for details</p>}
    </div>
  );
};

export default ScoreCard;
